import {
  ICompanyData,
  INoticeConfig,
  IFindIdData,
  IUserInfo,
  ICoordinateData,
  IQrCodeData,
  IEmailDomainInfo,
  ICertInfo,
  IShouldChangePasswordInfo,
  IUserRemainBookCount,
  IUserVerifyEmployeeNumber,
} from "@/domain/entities/user";
import { IHttpService } from "./../shared/httpService";
import { Result, combine, ok } from "neverthrow";
import { HttpError } from "./../shared/errors/httpError";
import { ParseError } from "./../shared/errors/parseError";
import { IBaseResponse, IBaseParser } from "./../shared/baseParser";
import {
  ILoginRequest,
  IUserRepository,
  HttpFindIdResponse,
  HttpLoginResponse,
  ILoginParser,
  INoticeConfigParser,
  IFindIdParser,
  HttpNoticeConfigResponse,
  HttpUserInfoResponse,
  IUserInfoParser,
  IChangePhoneNumberRequest,
  HttpSocialLoginResponse,
  ISocialLoginParser,
  HttpCompanyResponse,
  ICompanyParser,
  HttpCoordinateResponse,
  ICoordinateParser,
  HttpQrCodeResponse,
  IQrCodeParser,
  ITermsResult,
  HttpEmailDomainInfoResponse,
  IEmailDomainInfoParser,
  HttpCertInfoResponse,
  ICertInfoParser,
  IShouldChangePasswordInfoParser,
  HttpShouldPasswordChangeInfoResponse,
  HttpUserRemainBookCountResponse,
  IRemainBookCountParser,
  HttpVerifyEmployeeNumberResponse,
  IVerifyEmployeeNumberParser,
  IReportHistoryParser,
  HttpReportHistoryListResponse,
} from "./userRepository.types";
import { ACCESS_TOKEN, SERVICE_UID, SERVICE_CODE, RankCode } from "@/types";
import qs from "qs";
import useGetAccessToken from "@/use/useGetAccessToken";
import { IResultData } from "@/domain/entities/shared/result";
import { inject, injectable } from "inversify";
import TYPES from "@/ioc/identifiers";
import { ILoginData } from "@/domain/entities/login";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { IReportHistory } from "@/domain/entities/report";

@injectable()
export class UserRepository implements IUserRepository {
  @inject(TYPES.ILoginParser)
  private loginParser!: ILoginParser;

  @inject(TYPES.ISocialLoginParser)
  private socialLoginParser!: ISocialLoginParser;

  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  @inject(TYPES.INoticeConfigParser)
  private noticeConfigParser!: INoticeConfigParser;

  @inject(TYPES.IFindIdParser)
  private findIdParser!: IFindIdParser;

  @inject(TYPES.IUserInfoParser)
  private userInfoParser!: IUserInfoParser;

  @inject(TYPES.ICompanyParser)
  private companyParser!: ICompanyParser;

  @inject(TYPES.ICoordinateParser)
  private coordinateParser!: ICoordinateParser;

  @inject(TYPES.IQrCodeParser)
  private qrCodeParser!: IQrCodeParser;

  @inject(TYPES.IEmailDomainInfoParser)
  private emailDomainInfoParser!: IEmailDomainInfoParser;

  @inject(TYPES.ICertInfoParser)
  private certInfoParser!: ICertInfoParser;

  @inject(TYPES.IShouldChangePasswordInfoParser)
  private shouldChangePasswordInfoParser!: IShouldChangePasswordInfoParser;

  @inject(TYPES.IRemainBookCountParser)
  private remainBookCountParser!: IRemainBookCountParser;

  @inject(TYPES.IVerifyEmployeeNumberParser)
  private verifyEmployeeNumberParser!: IVerifyEmployeeNumberParser;

  @inject(TYPES.IReportHistoryParser)
  private reportHistoryParser!: IReportHistoryParser;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/public/service/${SERVICE_UID}/member`
    );
  }

  private getBaseUrl(isPublic: boolean) {
    const url = isPublic ? "/public" : "";

    return `${useGetProtocolHost()}/aibos/client/api/${
      process.env.VUE_APP_API_VERSION
    }${url}/service/${SERVICE_UID}/member`;
  }

  private getHeaders(
    isPublic: boolean,
    extraHeaders?: { [key: string]: string }
  ) {
    const token = useGetAccessToken();

    return isPublic
      ? extraHeaders
      : { ...extraHeaders, Authorization: `Bearer ${token}` };
  }

  async checkDuplicateId(
    loginId: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/check-loginid",
        data: qs.stringify({ loginId }),
        config: {
          headers: { "content-type": "application/x-www-form-urlencoded" },
        },
      },
      { parseTo }
    );
  }

  async sendEmailVerificationCode(
    email: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/generate-email-cert",
        data: qs.stringify({ email: email }),
        config: {
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async checkEmailVerificationCode(
    email: string,
    certNo: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/check-email-cert",
        data: qs.stringify({ email: email, certNo: certNo }),
        config: {
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async sendPhoneVerificationCode(
    phoneNumber: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const token = useGetAccessToken();
    const isPublic = !token;

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/generate-phone-cert",
        data: qs.stringify({ phone: phoneNumber }),
        config: {
          baseURL: this.getBaseUrl(isPublic),
          headers: this.getHeaders(isPublic, {
            "content-type": "application/x-www-form-urlencoded",
          }),
        },
      },
      { parseTo }
    );
  }

  async checkPhoneVerificationCode(
    phoneNumber: string,
    certNo: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const token = useGetAccessToken();
    const isPublic = !token;

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/check-phone-cert",
        data: qs.stringify({ phone: phoneNumber, certNo }),
        config: {
          baseURL: this.getBaseUrl(isPublic),
          headers: this.getHeaders(isPublic, {
            "content-type": "application/x-www-form-urlencoded",
          }),
        },
      },
      { parseTo }
    );
  }

  async verifyEmployeeNumber(
    eziokCertAuthValueEnc: string,
    employeeNo: string
  ): Promise<Result<IUserVerifyEmployeeNumber, ParseError | HttpError>> {
    const parseTo = (response: HttpVerifyEmployeeNumberResponse) => {
      return this.verifyEmployeeNumberParser.toDomain(response.data);
    };

    return await this.httpService.post<
      HttpVerifyEmployeeNumberResponse,
      IUserVerifyEmployeeNumber
    >(
      {
        url: "/employee-no/verify",
        config: {
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
        },
        data: {
          eziokCertAuthValueEnc,
          employeeNo,
        },
      },
      { parseTo }
    );
  }

  async signUp(
    rankCode: string,
    signUpValue: Record<string, any>,
    termsResult: ITermsResult[]
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const signUpRequest = {
      useRankCode: rankCode,
      ...signUpValue,
      termsResult,
    };
    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/signup",
        data: signUpRequest,
      },
      { parseTo }
    );
  }

  async login(
    userId: string,
    password: string,
    pushToken: string
  ): Promise<Result<ILoginData, ParseError | HttpError>> {
    const parseTo = (response: HttpLoginResponse) => {
      return this.loginParser.toDomain(response.data);
    };
    const data: ILoginRequest = {
      serviceCode: SERVICE_CODE,
      loginId: userId,
      password,
    };
    if (pushToken) {
      data.fcmToken = pushToken;
    }
    return await this.httpService.post<HttpLoginResponse, ILoginData>(
      {
        url: "/signin",
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/public/member`,
          headers: { "content-type": "application/x-www-form-urlencoded" },
        },
      },
      { parseTo }
    );
  }

  async loginWithKakao(
    redirectUri: string,
    authCode: string
  ): Promise<Result<ILoginData, ParseError | HttpError>> {
    const parseTo = (response: HttpSocialLoginResponse) => {
      return this.socialLoginParser.toDomain(response.data);
    };

    return await this.httpService.get<HttpSocialLoginResponse, ILoginData>(
      {
        url: "/kakao",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/public/service/${SERVICE_UID}/member/token`,
          params: {
            redirectUri,
            authCode,
          },
        },
      },
      { parseTo }
    );
  }

  async loginWithNaver(
    state: string,
    authCode: string
  ): Promise<Result<ILoginData, ParseError | HttpError>> {
    const parseTo = (response: HttpSocialLoginResponse) => {
      return this.socialLoginParser.toDomain(response.data);
    };

    return await this.httpService.get<HttpSocialLoginResponse, ILoginData>(
      {
        url: "/naver",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/public/service/${SERVICE_UID}/member/token`,
          params: {
            state,
            authCode,
          },
        },
      },
      { parseTo }
    );
  }

  async getNoticeConfig(): Promise<
    Result<INoticeConfig, ParseError | HttpError>
  > {
    const parseTo = (response: HttpNoticeConfigResponse) => {
      return this.noticeConfigParser.toDomain(response.data);
    };
    return await this.httpService.get<HttpNoticeConfigResponse, INoticeConfig>(
      {
        url: "/notice-config",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/member`,
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async setNoticeConfig(
    receiveNotice: boolean
  ): Promise<Result<INoticeConfig, ParseError | HttpError>> {
    const parseTo = (response: HttpNoticeConfigResponse) => {
      return this.noticeConfigParser.toDomain(response.data);
    };
    const data = {
      majorNoticeYn: receiveNotice,
    };
    return await this.httpService.put<HttpNoticeConfigResponse, INoticeConfig>(
      {
        url: "/notice-config",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/member`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
        data: qs.stringify(data),
      },
      { parseTo }
    );
  }

  async findId(
    userName: string,
    phoneNumber: string
  ): Promise<Result<IFindIdData, ParseError | HttpError>> {
    const parseTo = (response: HttpFindIdResponse) => {
      return this.findIdParser.toDomain(response.data);
    };
    const data = {
      name: userName,
      phone: phoneNumber,
    };
    return await this.httpService.post<HttpFindIdResponse, IFindIdData>(
      {
        url: "/find-loginid",
        data: qs.stringify(data),
        config: {
          headers: { "content-type": "application/x-www-form-urlencoded" },
        },
      },
      { parseTo }
    );
  }

  async findPassword(
    phoneNumber: string,
    userId?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      phone: phoneNumber,
      loginId: userId,
    };
    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/find-password",
        data: qs.stringify(data),
        config: {
          headers: { "content-type": "application/x-www-form-urlencoded" },
        },
      },
      { parseTo }
    );
  }

  async getUserInfo(): Promise<Result<IUserInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpUserInfoResponse) => {
      return this.userInfoParser.toDomain(response.data);
    };
    return await this.httpService.get<HttpUserInfoResponse, IUserInfo>(
      {
        url: "/member",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async changePassword(
    passwordValues: Record<string, any>
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/password",
        data: qs.stringify(passwordValues),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/member`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async updatePassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<Result<ILoginData, ParseError | HttpError>> {
    const parseTo = (response: HttpLoginResponse) => {
      return this.loginParser.toDomain(response.data);
    };
    const data = {
      loginId: userId,
      currPassword: currentPassword,
      nextPassword: newPassword,
      nextPasswordCert: newPassword,
    };

    return await this.httpService.put<HttpLoginResponse, ILoginData>(
      {
        url: "/password-expired",
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/public/service/${SERVICE_UID}/member`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
        },
      },
      { parseTo }
    );
  }

  async changePhoneNumber(
    phoneNumber: string,
    verificationCode: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data: IChangePhoneNumberRequest = {
      phone: phoneNumber,
      phoneCertNo: verificationCode,
    };

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/member",
        data: data,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async checkPassword(
    password: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.post<IBaseResponse, IResultData>(
      {
        url: "/check-password",
        data: qs.stringify({ password }),
        config: {
          baseURL: this.getBaseUrl(false),
          headers: this.getHeaders(false, {
            "content-type": "application/x-www-form-urlencoded",
          }),
        },
      },
      { parseTo }
    );
  }

  async checkIfPasswordNeedsChange(): Promise<
    Result<IShouldChangePasswordInfo, ParseError | HttpError>
  > {
    const parseTo = (response: HttpShouldPasswordChangeInfoResponse) => {
      return this.shouldChangePasswordInfoParser.toDomain(response.data);
    };

    return await this.httpService.get<
      HttpShouldPasswordChangeInfoResponse,
      IShouldChangePasswordInfo
    >(
      {
        url: "/password-interval",
        config: {
          baseURL: this.getBaseUrl(false),
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async changeMyInfo(
    myInfoValue: Record<string, any>
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    const requestBody = this.userInfoParser.fromDomain(myInfoValue);

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/member",
        data: requestBody,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async updateToken(
    memberUid: string,
    refreshToken: string
  ): Promise<Result<ILoginData, ParseError | HttpError>> {
    const accessToken =
      window.localStorage.getItem(ACCESS_TOKEN) ||
      window.sessionStorage.getItem(ACCESS_TOKEN);
    const parseTo = (response: HttpLoginResponse) => {
      return this.loginParser.toDomain(response.data);
    };
    return await this.httpService.post<HttpLoginResponse, ILoginData>(
      {
        url: "/refresh-token",
        data: qs.stringify({ memberUid, refreshToken }),
        config: {
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      },
      { parseTo }
    );
  }

  async updatePushToken(
    fcmToken: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/token",
        data: qs.stringify({ fcmToken }),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/member`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getCompanyList(
    rankCode: RankCode
  ): Promise<Result<ICompanyData[], ParseError | HttpError>> {
    const parseTo = (response: HttpCompanyResponse) => {
      const companyList = response.data.map(this.companyParser.toDomain);
      return combine(companyList);
    };
    return await this.httpService.get<HttpCompanyResponse, ICompanyData[]>(
      {
        url: `/company?rankCode=${rankCode}`,
        config: {
          // baseURL: `${useGetProtocolHost()}/aibos/client/api/${
          //   process.env.VUE_APP_API_VERSION
          // }/public/service/${SERVICE_UID}`,
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/public/service/202303181946191262511903507SVC`, // 테스트 용
        },
      },
      { parseTo }
    );
  }

  async getAddressCoordinate(
    roadAddress: string
  ): Promise<Result<ICoordinateData, ParseError | HttpError>> {
    const parseTo = (response: HttpCoordinateResponse) => {
      return this.coordinateParser.toDomain(response.documents[0]);
    };
    return await this.httpService.get<HttpCoordinateResponse, ICoordinateData>(
      {
        url: `address?query=${roadAddress}`,
        config: {
          baseURL: `https://dapi.kakao.com/v2/local/search`,
          headers: {
            Authorization: `KakaoAK ${process.env.VUE_APP_KAKAO_API_KEY}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getQrCode(): Promise<Result<IQrCodeData, ParseError | HttpError>> {
    const parseTo = (response: HttpQrCodeResponse) => {
      return this.qrCodeParser.toDomain(response.data);
    };
    return await this.httpService.post<HttpQrCodeResponse, IQrCodeData>(
      {
        url: `/mobileid`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/member`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getEmailDomains(): Promise<
    Result<IEmailDomainInfo[], ParseError | HttpError>
  > {
    const parseTo = (response: HttpEmailDomainInfoResponse) => {
      const domainInfos = response.data.map(
        this.emailDomainInfoParser.toDomain
      );
      return combine(domainInfos);
    };
    return await this.httpService.get<
      HttpEmailDomainInfoResponse,
      IEmailDomainInfo[]
    >(
      {
        url: "/email-domain",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/company`,
        },
      },
      { parseTo }
    );
  }

  async approveUserSignUp(
    certYn: boolean,
    token: string
  ): Promise<Result<ICertInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpCertInfoResponse) => {
      return this.certInfoParser.toDomain(response.data);
    };
    return await this.httpService.put<HttpCertInfoResponse, ICertInfo>(
      {
        url: `/manager-cert/token?certYn=${certYn}&token=${token}`,
      },
      { parseTo }
    );
  }

  async unlockAccount(
    loginId: string,
    eziokCertAuthValueEnc: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/active?loginId=${loginId}`,
        data: { eziokCertAuthValueEnc },
      },
      { parseTo }
    );
  }

  async extendPasswordExpiration(
    memberUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };

    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: `/${memberUid}/password-change-next`,
        data: {},
        config: {
          baseURL: this.getBaseUrl(false),
          headers: this.getHeaders(false, {
            "content-type": "application/json",
          }),
        },
      },
      { parseTo }
    );
  }

  async getRemainBookCount(): Promise<
    Result<IUserRemainBookCount, ParseError | HttpError>
  > {
    const parseTo = (response: HttpUserRemainBookCountResponse) => {
      return this.remainBookCountParser.toDomain(response);
    };

    return await this.httpService.get<
      HttpUserRemainBookCountResponse,
      IUserRemainBookCount
    >(
      {
        url: "/remain-count",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/book`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getReportHistory(
    fromDate: string,
    toDate: string
  ): Promise<Result<IReportHistory[], ParseError | HttpError>> {
    const parseTo = (resposne: HttpReportHistoryListResponse) => {
      return combine(resposne.data.map(this.reportHistoryParser.toDomain));
    };

    const params = `?driveDateFrom=${fromDate}&driveDateTo=${toDate}&sortOrder=driveDate desc`;

    return await this.httpService.get<
      HttpReportHistoryListResponse,
      IReportHistory[]
    >(
      {
        url: `/report${params}`,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}/voc`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }
}
